'use client';

import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SGhostButton } from '../SGhostButton';
import { type SIconName } from '../SIcon';
import { cn } from '../../lib/cn';
import { AUTOFILL_RESET_CLASS } from '../../lib/autofill';
import { type SColor } from '../../lib/color';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export interface SInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'value' | 'onChange' | 'type' | 'prefix' | 'suffix'
> {
  /** 값 (제어) */
  value?: string | number;
  /** 값 변경 (sdUpdate) — 문자열 전달 */
  onValueChange?: (value: string) => void;
  /** 네이티브 onChange (form-agnostic 연동용, RHF 등) */
  onChange?: InputHTMLAttributes<HTMLInputElement>['onChange'];
  /** 입력 타입 */
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'search' | 'url';
  /** 크기 */
  size?: SFieldSize;
  /** 좌측 아이콘명 */
  icon?: SIconName;
  /** 좌측 아이콘 색상 */
  iconColor?: SColor;
  /** 입력 앞(좌측) 커스텀 노드 — 원본 slot="prefix" */
  prefix?: ReactNode;
  /** 입력 뒤(우측) 커스텀 노드 — 원본 slot="suffix" */
  suffix?: ReactNode;
  /** 지우기 버튼 */
  clearable?: boolean;
  /** 유효성 규칙 — blur 시 자동 검증 */
  rules?: Rule[];
  /** 필드 상태 ('default' | 'pass' | 'error') */
  status?: SFieldStatus;
  /** 포커스 상태 (제어/반영) */
  focused?: boolean;
  /** 호버 상태 (제어/반영) */
  hovered?: boolean;
  /** 내부 input 요소 className */
  inputClass?: string;
  /** 내부 input 요소 style */
  inputStyle?: CSSProperties;
  // ── SField 래퍼 ──
  label?: string;
  labelWidth?: number | string;
  /** 레이블 툴팁 텍스트 */
  labelTooltip?: string;
  /** 레이블 툴팁 상세 옵션 */
  labelTooltipProps?: Partial<STooltipProps>;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  addonLabel?: string;
  /** 어드온 정렬 */
  addonAlign?: SFieldAddonAlign;
  width?: number | string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

// 원본 textinput.{sm,md}.typography: sm 12/20, md 14/24 (fontWeight 400)
const FONT: Record<SFieldSize, { fontSize: number; lineHeight: number; paddingX: string }> = {
  sm: { fontSize: 12, lineHeight: 20, paddingX: 'var(--cmp-textinput-sm-paddingX)' },
  md: { fontSize: 14, lineHeight: 24, paddingX: 'var(--cmp-textinput-md-paddingX)' },
};

/** SInput — sd-input 포팅. SField(테두리 박스) + borderless native input + clearable/password/icon. */
export const SInput = forwardRef<HTMLInputElement, SInputProps>(function SInput(
  {
    value,
    onValueChange,
    onChange,
    type = 'text',
    size = 'sm',
    icon,
    iconColor,
    prefix,
    suffix,
    clearable = false,
    rules,
    status,
    focused,
    hovered,
    inputClass,
    inputStyle,
    label,
    labelWidth,
    labelTooltip,
    labelTooltipProps,
    hint,
    error,
    errorMessage,
    addonLabel,
    addonAlign,
    width,
    disabled = false,
    readOnly = false,
    placeholder = '입력해 주세요.',
    onBlur,
    className,
    style,
    ...rest
  },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const f = FONT[size];
  const isPassword = type === 'password';
  const inputType = isPassword ? (visible ? 'text' : 'password') : type;
  const hasValue = value !== undefined && value !== null && String(value) !== '';

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name: rest.name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  // rules가 있으면 blur 시 검증 → error/errorMessage 자동 결정
  const resolvedError = error || ruleError !== '';
  const resolvedErrorMessage = ruleError !== '' ? ruleError : errorMessage;
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      icon={icon}
      iconColor={iconColor}
      size={size}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused}
      hovered={hovered}
      errorMessage={resolvedErrorMessage}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      width={width}
      disabled={disabled}
      readOnly={readOnly}
      className={className}
      style={style}
    >
      <div
        className={cn(
          'flex h-full w-full items-center',
          size === 'md' ? 'gap-[var(--cmp-textinput-md-gap)]' : 'gap-[var(--cmp-textinput-sm-gap)]',
        )}
        // 원본 .sd-input__content 처럼 컨테이너에 폰트 지정 → input·prefix·suffix 모두 상속
        style={{
          padding: `0 ${f.paddingX}`,
          fontSize: f.fontSize,
          lineHeight: `${f.lineHeight}px`,
        }}
      >
        {/* icon prop은 원본처럼 SField 라벨 영역에 렌더됨. 입력 좌측 노드는 prefix 사용 */}
        {prefix != null && <span className="flex-shrink-0">{prefix}</span>}
        <input
          ref={ref}
          type={inputType}
          // onChange 가 항상 부착되므로 value 를 항상 지정해 controlled 로 고정한다
          // (value 미지정 시 uncontrolled ↔ controlled 전환 경고 방지).
          value={value ?? ''}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          onChange={e => {
            onChange?.(e);
            onValueChange?.(e.target.value);
            if (ruleError !== '') setRuleError(runRules(e.target.value, rules));
          }}
          onBlur={e => {
            onBlur?.(e);
            if (rules && rules.length > 0) setRuleError(runRules(e.target.value, rules));
          }}
          className={cn(
            'h-full min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-[color:var(--sys-color-field-text-placeholder)] disabled:cursor-not-allowed',
            AUTOFILL_RESET_CLASS,
            inputClass,
          )}
          style={{
            // fontSize/lineHeight 는 컨테이너에서 상속 (원본 font-size: inherit)
            color: disabled
              ? 'var(--cmp-textinput-text-disabled)'
              : 'var(--sys-color-field-text-default)',
            ...inputStyle,
          }}
          {...rest}
        />
        {suffix != null && <span className="flex-shrink-0">{suffix}</span>}
        {/* 원본 sd-input: clear/password 는 sd-ghost-button(xxs) */}
        {clearable && hasValue && !disabled && !readOnly && (
          <SGhostButton
            icon="close"
            size="xxs"
            ariaLabel="지우기"
            className="flex-shrink-0"
            onClick={() => onValueChange?.('')}
          />
        )}
        {isPassword && (
          <SGhostButton
            icon={visible ? 'visibilityOn' : 'visibilityOff'}
            size="xxs"
            ariaLabel="비밀번호 표시"
            disabled={disabled || readOnly}
            className="flex-shrink-0"
            onClick={() => setVisible(v => !v)}
          />
        )}
      </div>
    </SField>
  );
});
